import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const TenantOnboarding = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successResult, setSuccessResult] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Step 1: Client Identity
    tenantCode: '',
    organizationName: '',
    displayName: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    country: 'India',
    timeZone: 'Asia/Kolkata',
    currency: 'INR',

    // Step 2: Deployment & Isolation
    isolationModel: 'SeparateDatabase',
    primaryDomain: '',
    databaseServer: '127.0.0.1',
    databasePort: 3333,
    databaseName: '',
    databaseUser: 'lis_app',
    databasePassword: '',

    // Step 3: Subscription & Plan
    planCode: 'PROFESSIONAL',
    isTrial: false,
    billingCycle: 'MONTHLY',
    maxCentres: 5,
    maxUsers: 25,

    // Step 4: Initial Clinical Configuration
    initialCentreCode: 'MAIN',
    initialCentreName: 'Main Laboratory Branch',
    enabledModules: ['PATHOLOGY', 'PHLEBOTOMY', 'BILLING']
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const val = type === 'checkbox' ? checked : value;
      const next = { ...prev, [name]: val };
      if (name === 'tenantCode') {
        const cleanCode = value.trim().toUpperCase();
        next.tenantCode = cleanCode;
        if (!prev.displayName || prev.displayName === prev.organizationName) {
          next.displayName = prev.organizationName;
        }
        if (!prev.databaseName || prev.databaseName.startsWith('lis_tenant_')) {
          next.databaseName = cleanCode ? `lis_tenant_${cleanCode.toLowerCase()}_test` : '';
        }
        if (!prev.primaryDomain || prev.primaryDomain.endsWith('.lims.local')) {
          next.primaryDomain = cleanCode ? `${cleanCode.toLowerCase()}.lims.local` : '';
        }
      }
      return next;
    });
  };

  const handleToggleModule = (mod) => {
    setFormData(prev => {
      const list = prev.enabledModules.includes(mod)
        ? prev.enabledModules.filter(m => m !== mod)
        : [...prev.enabledModules, mod];
      return { ...prev, enabledModules: list };
    });
  };

  const validateStep = (currentStep) => {
    setError(null);
    if (currentStep === 1) {
      if (!formData.tenantCode || formData.tenantCode.length < 2) {
        setError('Tenant code is required (at least 2 alphanumeric characters).');
        return false;
      }
      if (!/^[A-Z0-9_-]+$/.test(formData.tenantCode)) {
        setError('Tenant code must contain only uppercase letters, numbers, hyphens, or underscores.');
        return false;
      }
      if (!formData.organizationName || formData.organizationName.trim().length < 3) {
        setError('Legal organization name is required (at least 3 characters).');
        return false;
      }
    } else if (currentStep === 2) {
      if (!formData.primaryDomain || formData.primaryDomain.trim().length < 3) {
        setError('Primary domain is required.');
        return false;
      }
      if (formData.primaryDomain.includes(' ') || formData.primaryDomain.includes('/')) {
        setError('Domain must be a valid hostname without spaces or slashes.');
        return false;
      }
      if (!formData.databaseServer) {
        setError('Database server host is required.');
        return false;
      }
      if (!formData.databaseName) {
        setError('Database name is required.');
        return false;
      }
      if (!formData.databasePassword || formData.databasePassword.length < 6) {
        setError('Database password is required and must be at least 6 characters long.');
        return false;
      }
    } else if (currentStep === 3) {
      if (!formData.planCode) {
        setError('Please select a commercial subscription plan.');
        return false;
      }
    } else if (currentStep === 4) {
      if (!formData.initialCentreName) {
        setError('Initial centre name is required.');
        return false;
      }
      if (formData.enabledModules.length === 0) {
        setError('At least one clinical module must be enabled.');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setError(null);
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleOnboardSubmit = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(4)) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        tenantCode: formData.tenantCode.trim().toUpperCase(),
        organizationName: formData.organizationName.trim(),
        displayName: formData.displayName?.trim() || formData.organizationName.trim(),
        contactPerson: formData.contactPerson?.trim() || 'Laboratory Administrator',
        contactEmail: formData.contactEmail?.trim() || `admin@${formData.primaryDomain.trim()}`,
        contactPhone: formData.contactPhone?.trim() || '+91-9876543210',
        country: formData.country,
        timeZone: formData.timeZone,
        currency: formData.currency,
        primaryDomain: formData.primaryDomain.trim().toLowerCase(),
        isolationModel: formData.isolationModel,
        databaseServer: formData.databaseServer.trim(),
        databasePort: parseInt(formData.databasePort, 10) || 3306,
        databaseName: formData.databaseName.trim(),
        databaseUser: formData.databaseUser.trim(),
        databasePassword: formData.databasePassword
      };

      const res = await saasPlatformService.onboardTenant(payload);
      const createdTenant = res.data || res;

      // If subscription was selected, configure subscription
      try {
        if (createdTenant.tenantId) {
          await saasPlatformService.updateTenantSubscription(createdTenant.tenantId, {
            planCode: formData.planCode,
            status: 'ACTIVE',
            billingCycle: formData.billingCycle,
            isTrial: formData.isTrial,
            maxCentres: parseInt(formData.maxCentres, 10) || 5,
            maxUsers: parseInt(formData.maxUsers, 10) || 25,
            changeNotes: 'Initial subscription provisioned during onboarding wizard'
          });

          // Top up initial welcome credit
          await saasPlatformService.topupTenantWallet(createdTenant.tenantId, {
            amount: 500,
            transactionType: 'PROMOTIONAL',
            reason: 'Welcome onboarding credit grant',
            idempotencyKey: 'ONBOARD-' + createdTenant.tenantId + '-' + Date.now()
          });
        }
      } catch (subErr) {
        console.warn('Subscription/wallet initialization notice:', subErr);
      }

      setSuccessResult(createdTenant);
    } catch (err) {
      console.error('Failed to onboard tenant', err);
      setError(err?.response?.data?.message || err?.message || 'Provisioning failed. Check catalog connectivity and inputs.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🚀</span> Enterprise Client Onboarding Wizard
          </h1>
          <p>Register Client Identity, Isolated Database, Commercial Subscription &amp; Clinical Modules</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div>
            <strong>Validation / Provisioning Alert:</strong> {error}
          </div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {successResult ? (
        <div className="saas-card" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '2.5rem' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🎉</div>
          <h2 style={{ color: '#047857', margin: '0 0 0.5rem 0' }}>Client Onboarded &amp; Provisioned Successfully!</h2>
          <p style={{ color: '#64748b', fontSize: '1rem', marginBottom: '2rem' }}>
            The tenant account is active in the central catalog, database credentials are encrypted with AES-256-GCM, and the commercial subscription has been activated.
          </p>

          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem', textAlign: 'left', marginBottom: '2rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
              <div><strong>Tenant ID:</strong> #{successResult.tenantId}</div>
              <div><strong>Tenant Code:</strong> <span style={{ color: '#0284c7', fontWeight: 'bold' }}>{successResult.tenantCode}</span></div>
              <div><strong>Organization:</strong> {successResult.organizationName}</div>
              <div><strong>Primary Domain:</strong> <span style={{ fontFamily: 'monospace' }}>{successResult.primaryDomain}</span></div>
              <div><strong>Database:</strong> <span style={{ fontFamily: 'monospace' }}>{successResult.databaseName}</span></div>
              <div>
                <strong>Status:</strong>{' '}
                <span className={`saas-badge ${successResult.status === 'ACTIVE' ? 'saas-badge-active' : 'saas-badge-warning'}`}>
                  {successResult.status || 'ACTIVE'}
                </span>
              </div>
            </div>

            {Array.isArray(successResult.executionSteps) && successResult.executionSteps.length > 0 && (
              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: '600', fontSize: '0.8125rem', color: '#475569', marginBottom: '0.5rem' }}>
                  PROVISIONING EXECUTION AUDIT PIPELINE:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {successResult.executionSteps.map((s, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                      <span style={{ color: s.status === 'COMPLETED' ? '#10b981' : '#f59e0b', fontWeight: 'bold' }}>
                        {s.status === 'COMPLETED' ? '✓' : '⚠️'}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#1e293b' }}>{s.stepName}:</span>
                      <span style={{ color: '#64748b' }}>{s.details}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <button className="saas-btn saas-btn-primary" onClick={() => navigate('/saas/tenants')}>
              🏢 View All Tenants
            </button>
            <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/clinical')}>
              🔬 Configure Clinical Reference Ranges
            </button>
          </div>
        </div>
      ) : (
        <div className="saas-card" style={{ maxWidth: '850px', margin: '0 auto' }}>
          {/* 5-Step Progress Indicator */}
          <div style={{ padding: '1.5rem 1.5rem 0 1.5rem' }}>
            <div className="saas-wizard-steps">
              <div className={`saas-wizard-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`} onClick={() => step > 1 && setStep(1)}>
                <div className="saas-wizard-step-circle">{step > 1 ? '✓' : '1'}</div>
                <span className="saas-wizard-step-label">1. Client Identity</span>
              </div>
              <div className={`saas-wizard-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`} onClick={() => step > 2 && setStep(2)}>
                <div className="saas-wizard-step-circle">{step > 2 ? '✓' : '2'}</div>
                <span className="saas-wizard-step-label">2. Deployment</span>
              </div>
              <div className={`saas-wizard-step ${step === 3 ? 'active' : step > 3 ? 'completed' : ''}`} onClick={() => step > 3 && setStep(3)}>
                <div className="saas-wizard-step-circle">{step > 3 ? '✓' : '3'}</div>
                <span className="saas-wizard-step-label">3. Subscription</span>
              </div>
              <div className={`saas-wizard-step ${step === 4 ? 'active' : step > 4 ? 'completed' : ''}`} onClick={() => step > 4 && setStep(4)}>
                <div className="saas-wizard-step-circle">{step > 4 ? '✓' : '4'}</div>
                <span className="saas-wizard-step-label">4. Clinical Setup</span>
              </div>
              <div className={`saas-wizard-step ${step === 5 ? 'active' : ''}`}>
                <div className="saas-wizard-step-circle">5</div>
                <span className="saas-wizard-step-label">5. Validation</span>
              </div>
            </div>
          </div>

          <div className="saas-card-body" style={{ padding: '1.5rem 2rem' }}>
            {/* Step 1: Client Identity */}
            {step === 1 && (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Step 1: Client Identity &amp; Contact</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Legal Business / Lab Name *</label>
                    <input
                      type="text"
                      name="organizationName"
                      className="saas-form-input"
                      placeholder="e.g. Apollo Diagnostics Hub"
                      value={formData.organizationName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Unique Tenant Code (2-16 chars) *</label>
                    <input
                      type="text"
                      name="tenantCode"
                      className="saas-form-input"
                      placeholder="e.g. APOLLO_LAB"
                      value={formData.tenantCode}
                      onChange={handleChange}
                      required
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Display / Brand Name</label>
                    <input
                      type="text"
                      name="displayName"
                      className="saas-form-input"
                      placeholder="e.g. Apollo Diagnostics"
                      value={formData.displayName}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Contact Person</label>
                    <input
                      type="text"
                      name="contactPerson"
                      className="saas-form-input"
                      placeholder="e.g. Dr. Rajesh Varma"
                      value={formData.contactPerson}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Contact Email</label>
                    <input
                      type="email"
                      name="contactEmail"
                      className="saas-form-input"
                      placeholder="admin@apollolab.com"
                      value={formData.contactEmail}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Contact Phone</label>
                    <input
                      type="text"
                      name="contactPhone"
                      className="saas-form-input"
                      placeholder="+91-9876543210"
                      value={formData.contactPhone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Country</label>
                    <select name="country" className="saas-form-input" value={formData.country} onChange={handleChange}>
                      <option value="India">India</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                    </select>
                  </div>
                  <div>
                    <label className="saas-form-label">Time Zone</label>
                    <select name="timeZone" className="saas-form-input" value={formData.timeZone} onChange={handleChange}>
                      <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                      <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                      <option value="America/New_York">America/New_York (EST)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                  <div>
                    <label className="saas-form-label">Currency</label>
                    <select name="currency" className="saas-form-input" value={formData.currency} onChange={handleChange}>
                      <option value="INR">INR (₹)</option>
                      <option value="AED">AED (د.إ)</option>
                      <option value="USD">USD ($)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Deployment & Isolation */}
            {step === 2 && (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Step 2: Deployment &amp; Database Isolation</h3>
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '1rem', borderRadius: '6px', marginBottom: '1.25rem', fontSize: '0.8125rem', color: '#0369a1' }}>
                  <strong>Enterprise Isolation Safeguard:</strong><br />
                  Every client laboratory operates inside a strictly isolated MySQL database (`SeparateDatabase` tier). Database passwords are automatically encrypted via envelope encryption (AES-256-GCM) with AAD authentication.
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Primary Routing Domain *</label>
                  <input
                    type="text"
                    name="primaryDomain"
                    className="saas-form-input"
                    placeholder="e.g. apollo.lims.local"
                    value={formData.primaryDomain}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Database Server Host *</label>
                    <input
                      type="text"
                      name="databaseServer"
                      className="saas-form-input"
                      value={formData.databaseServer}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Port</label>
                    <input
                      type="number"
                      name="databasePort"
                      className="saas-form-input"
                      value={formData.databasePort}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">MySQL Application Database Name *</label>
                  <input
                    type="text"
                    name="databaseName"
                    className="saas-form-input"
                    value={formData.databaseName}
                    onChange={handleChange}
                    required
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Database Username *</label>
                    <input
                      type="text"
                      name="databaseUser"
                      className="saas-form-input"
                      value={formData.databaseUser}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Database Password *</label>
                    <input
                      type="password"
                      name="databasePassword"
                      className="saas-form-input"
                      placeholder="Min 6 characters"
                      value={formData.databasePassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Subscription & Commercial Plan */}
            {step === 3 && (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Step 3: Commercial Plan &amp; Entitlements</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div
                    onClick={() => setFormData({ ...formData, planCode: 'STARTER', maxCentres: 1, maxUsers: 5 })}
                    style={{
                      border: formData.planCode === 'STARTER' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: 'pointer',
                      background: formData.planCode === 'STARTER' ? '#f0f9ff' : '#ffffff'
                    }}
                  >
                    <strong style={{ fontSize: '1rem', color: '#0f172a' }}>Starter</strong>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0284c7', margin: '0.25rem 0' }}>₹4,999 /mo</div>
                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>Single centre, 5 licensed staff, core pathology &amp; phlebotomy.</p>
                  </div>

                  <div
                    onClick={() => setFormData({ ...formData, planCode: 'PROFESSIONAL', maxCentres: 5, maxUsers: 25 })}
                    style={{
                      border: formData.planCode === 'PROFESSIONAL' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: 'pointer',
                      background: formData.planCode === 'PROFESSIONAL' ? '#f0f9ff' : '#ffffff'
                    }}
                  >
                    <strong style={{ fontSize: '1rem', color: '#0f172a' }}>Professional</strong>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0284c7', margin: '0.25rem 0' }}>₹14,999 /mo</div>
                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>5 branches, 25 staff, pathology, radiology &amp; billing.</p>
                  </div>

                  <div
                    onClick={() => setFormData({ ...formData, planCode: 'ENTERPRISE', maxCentres: 50, maxUsers: 500 })}
                    style={{
                      border: formData.planCode === 'ENTERPRISE' ? '2px solid #0284c7' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: 'pointer',
                      background: formData.planCode === 'ENTERPRISE' ? '#f0f9ff' : '#ffffff'
                    }}
                  >
                    <strong style={{ fontSize: '1rem', color: '#0f172a' }}>Enterprise</strong>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0284c7', margin: '0.25rem 0' }}>₹39,999 /mo</div>
                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>Unlimited branches, B2B portal, custom reference ranges.</p>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Billing Cycle</label>
                    <select name="billingCycle" className="saas-form-input" value={formData.billingCycle} onChange={handleChange}>
                      <option value="MONTHLY">Monthly Billing</option>
                      <option value="QUARTERLY">Quarterly Billing (5% discount)</option>
                      <option value="ANNUAL">Annual Billing (15% discount)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.8rem', cursor: 'pointer', fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        name="isTrial"
                        checked={formData.isTrial}
                        onChange={handleChange}
                      />
                      14-Day Free Trial Mode
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Initial Clinical Configuration */}
            {step === 4 && (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Step 4: Initial Facility &amp; Clinical Configuration</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label className="saas-form-label">Initial Centre Code</label>
                    <input
                      type="text"
                      name="initialCentreCode"
                      className="saas-form-input"
                      value={formData.initialCentreCode}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Initial Facility / Branch Name</label>
                    <input
                      type="text"
                      name="initialCentreName"
                      className="saas-form-input"
                      value={formData.initialCentreName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Enabled Subsystems &amp; Clinical Modules</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
                    {[
                      { id: 'PATHOLOGY', label: '🔬 Pathology & Clinical Labs' },
                      { id: 'PHLEBOTOMY', label: '🩸 Phlebotomy & Accessioning' },
                      { id: 'BILLING', label: '💳 Patient & Corporate Billing' },
                      { id: 'RADIOLOGY', label: '🩻 Radiology & Imaging DICOM' },
                      { id: 'B2B_PORTAL', label: '🏢 B2B Hospital Client Portal' }
                    ].map(mod => (
                      <label
                        key={mod.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem',
                          borderRadius: '6px',
                          border: formData.enabledModules.includes(mod.id) ? '1px solid #0284c7' : '1px solid #e2e8f0',
                          background: formData.enabledModules.includes(mod.id) ? '#f0f9ff' : '#ffffff',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.enabledModules.includes(mod.id)}
                          onChange={() => handleToggleModule(mod.id)}
                        />
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{mod.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Validation & Review */}
            {step === 5 && (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Step 5: Review &amp; Commit Provisioning</h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem', fontSize: '0.8125rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <span style={{ color: '#64748b' }}>Client Name:</span><br />
                      <strong>{formData.organizationName}</strong> ({formData.tenantCode})
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Primary Domain:</span><br />
                      <strong style={{ fontFamily: 'monospace' }}>{formData.primaryDomain}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Database:</span><br />
                      <strong style={{ fontFamily: 'monospace' }}>{formData.databaseName}</strong> on {formData.databaseServer}:{formData.databasePort}
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Subscription Tier:</span><br />
                      <strong style={{ color: '#0284c7' }}>{formData.planCode}</strong> ({formData.billingCycle})
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Initial Branch:</span><br />
                      <strong>{formData.initialCentreName}</strong> ({formData.initialCentreCode})
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Modules:</span><br />
                      <strong>{formData.enabledModules.join(', ')}</strong>
                    </div>
                  </div>
                </div>

                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '1rem', borderRadius: '6px', color: '#166534', fontSize: '0.8125rem' }}>
                  <strong>Automated Onboarding Actions:</strong><br />
                  1. Tenant record created in `platform_tenant`<br />
                  2. Database credentials encrypted with AES-256-GCM and stored in `platform_database_mapping`<br />
                  3. Commercial subscription activated with quotas in `platform_tenant_subscription`<br />
                  4. Initial prepaid wallet and welcome credit grant created in `platform_tenant_wallet`
                </div>
              </div>
            )}

            {/* Wizard Navigation Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              {step > 1 ? (
                <button type="button" className="saas-btn saas-btn-secondary" onClick={prevStep} disabled={loading}>
                  ← Previous Step
                </button>
              ) : <div />}

              {step < 5 ? (
                <button type="button" className="saas-btn saas-btn-primary" onClick={nextStep}>
                  Next Step →
                </button>
              ) : (
                <button type="button" className="saas-btn saas-btn-success" onClick={handleOnboardSubmit} disabled={loading}>
                  {loading ? 'Provisioning Client Fleet...' : '🚀 Commit & Provision Client'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantOnboarding;
